import {useCallback, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {useTheme, type Theme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useAuth} from 'oidc-react';

import {routes} from '../constants/routes';
import {useDialog} from '../hooks/useDialog';
import {useIsMobile} from '../hooks/useIsMobile';
import {useAuthStore} from '../stores/authStore';
import {useOrganisationStore} from '../stores/organisationStore';
import {useUserStore} from '../stores/userStore';

export const useAppShell = (): {
  theme: Theme;
  isMobile: boolean;
  mobileNavOpen: boolean;
  toggleMobileNav: () => void;
  name: string | null | undefined;
  organisationName: string;
  organisationGroupName: string;
  openSelectOrganisation: boolean;
  onGoToProfile: () => void;
  onOpenSelectOrganisation: () => void;
  onCloseSelectOrganisation: () => void;
  handleLogout: () => Promise<void>;
  logoutDialogOpen: boolean;
  onOpenLogoutDialog: () => void;
  onCloseLogoutDialog: () => void;
  isNarrow: boolean;
} => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isNarrow = useMediaQuery(theme.breakpoints.down('lg'));

  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const toggleMobileNav = useCallback(() => {
    setMobileNavOpen((o) => !o);
  }, []);

  const logout = useAuthStore((state) => state.logout);
  const name = useUserStore((state) => state.name);
  const organisationName = useOrganisationStore(
    (state) => state.organisationName,
  );
  const organisationGroupName = useOrganisationStore(
    (state) => state.organisationGroupName,
  );
  const {signOut, signOutRedirect} = useAuth();

  const [openSelectOrganisation, setOpenSelectOrganisation] = useState(false);
  const {
    isOpen: logoutDialogOpen,
    onOpen: onOpenLogoutDialog,
    onClose: onCloseLogoutDialog,
  } = useDialog();
  const navigate = useNavigate();

  const onGoToProfile = useCallback(() => {
    navigate(routes.profile);
  }, [navigate]);
  const onOpenSelectOrganisation = useCallback(() => {
    setOpenSelectOrganisation(true);
  }, []);
  const onCloseSelectOrganisation = useCallback(() => {
    setOpenSelectOrganisation(false);
  }, []);

  const handleLogout = useCallback(async () => {
    logout();
    await signOutRedirect({redirectMethod: 'replace'});
    await signOut();
  }, [logout, signOut, signOutRedirect]);

  return {
    theme,
    isMobile,
    mobileNavOpen,
    toggleMobileNav,
    name,
    organisationName,
    organisationGroupName,
    openSelectOrganisation,
    onGoToProfile,
    onOpenSelectOrganisation,
    onCloseSelectOrganisation,
    handleLogout,
    logoutDialogOpen,
    onOpenLogoutDialog,
    onCloseLogoutDialog,
    isNarrow,
  };
};
